// src/Routes/shared.tsx
import React from 'react';
import { withLoading } from '@app/hocs/withLoading.hoc';
import RequireAuth from '@app/components/router/RequireAuth';
import MainLayout from '@app/components/layouts/main/MainLayout/MainLayout';
import { ForcePasswordChangeRedirect } from '@app/components/ForcePasswordChangeRedirect';
import MainDashboardPage from '@app/pages/DashboardPages/MainDashboardPage';
import ShortcutMenuPage from '@app/pages/ShortcutMenuPage';
import { ListReportsPage } from '@app/pages/Reports/ListReportsPage';
import { ViewReportPage } from '@app/pages/Reports/ViewReportPage';
import { ListReportCategoriesPage } from '@app/pages/Reports/ListReportCategoriesPage';
import LoginPage from '@app/pages/LoginPage';
import LoginPage2 from '@app/pages/LoginPage2';
import SignUpPage from '@app/pages/SignUpPage';
import ForgotPasswordPage from '@app/pages/ForgotPasswordPage';
import SecurityCodePage from '@app/pages/SecurityCodePage';
import NewPasswordPage from '@app/pages/NewPasswordPage';
import LockPage from '@app/pages/LockPage';
import NotificationCenterPage from '@app/pages/NotificationCenter/NotificationPage';
import AuthLayout from '@app/components/layouts/AuthLayout/AuthLayout';

import type { RouteConfig } from '@app/components/header/components/Breadcrumb/DynamicBreadcrumb';

// lazy wrappers you already use
const MainDashboard = withLoading(MainDashboardPage);
const ShortcutMenu = withLoading(ShortcutMenuPage);
const AuthLayoutFallback = withLoading(React.lazy(() => import('@app/components/layouts/AuthLayout/AuthLayout')));
const LogoutFallback = withLoading(React.lazy(() => import('../components/router/Logout')));
const NotificationCenter = withLoading(React.lazy(() => import('@app/pages/NotificationCenter/NotificationPage')));

// keep your protected layout as-is
export const protectedLayout = (
  <RequireAuth>
    <ForcePasswordChangeRedirect>
      <MainLayout />
    </ForcePasswordChangeRedirect>
  </RequireAuth>
);

// children that both variants share under "/"
export const sharedRootChildren: RouteConfig[] = [
  { index: true, element: <MainDashboard />, breadcrumb: 'Main Dashboard' },
  { path: 'notification-center', element: <NotificationCenter />, breadcrumb: 'Notification Center' },
  { path: 'shortcut/:category', element: <ShortcutMenu />, breadcrumb: 'Shortcut' },

  // Reports subtree shared by both
  {
    path: 'reports',
    breadcrumb: 'Reports',
    children: [
      { path: 'categories', element: <ListReportCategoriesPage />, breadcrumb: 'Categories' },
      { path: ':id', element: <ListReportsPage />, breadcrumb: 'Reports' },
      { path: ':id/:systemName', element: <ViewReportPage />, breadcrumb: 'View Report' },
    ],
  },
];

// auth + logout (shared)
export function getAuthAndLogout(variant: 'control' | 'user' | 'store'): RouteConfig[] {
  const loginPath = variant === 'user' ? 'login-user' : variant === 'store' ? 'login-store' : 'login';

  return [
    {
      path: 'auth',
      breadcrumb: 'Authentication',
      element: <AuthLayoutFallback />,
      children: [
        { path: loginPath, element: <LoginPage />, breadcrumb: 'Login' },
        { path: 'sign-up', element: <SignUpPage />, breadcrumb: 'Sign Up' },
        { path: 'lock', element: <RequireAuth><LockPage /></RequireAuth>, breadcrumb: 'Lock' },
        { path: 'forgot-password', element: <ForgotPasswordPage />, breadcrumb: 'Forgot Password' },
        { path: 'security-code', element: <SecurityCodePage />, breadcrumb: 'Security Code' },
        { path: 'new-password', element: <NewPasswordPage />, breadcrumb: 'New Password' },
      ],
    },
    { path: 'logout', breadcrumb: 'Logout', element: <LogoutFallback /> },
  ];
}